import React, { useEffect, useRef, useState } from 'react'
import { useInvestors } from './hooks'
import { FilterValue } from 'antd/es/table/interface'
import { Button, Input, InputRef, Space, Table } from 'antd'
import { CCardBody } from '@coreui/react-pro'
import { ColumnsType } from 'antd/lib/table'
import { path } from '../../../../../libs'
import { Account, DataIndex, Investor } from './types'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'

type Props = {
  strategyId: number
}
export const InvestorsList: React.FC<Props> = ({ strategyId }) => {
  const { investors, totalCountOfInvestors, loading, onChangeInvestorsList } =
    useInvestors(strategyId)
  const [changed, setChanged] = useState(false)

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('startedAt')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    if (changed) {
      onChangeInvestorsList({
        strategyId: strategyId,
        limit: pageSize,
        page: page,
        orderBy: orderBy,
        orderDirection: orderDirection,
        searchText: searchText,
      })
    } else {
      setChanged(true)
    }
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<Investor> = [
    {
      title: 'Client',
      ...getColumnSearchProps('client.fullName'),
      render: (InvestorItem: Investor) => (
        <a
          href={path.clients.details(String(InvestorItem.clientId))}
          target="_blank"
        >
          {InvestorItem.client.fullName}
        </a>
      ),
      sorter: false,
    },
    {
      title: 'MT Login',
      dataIndex: 'account',
      ...getColumnSearchProps('account.mtLogin'),
      sorter: false,
      render: (account: Account) =>
        account?.accountPlatform + ' ' + account?.mtLogin,
    },
    {
      title: 'Сopy ratio',
      dataIndex: 'proportion',
      key: 'proportion',
      render: (proportion: number) => proportion,
    },
    {
      title: 'Start',
      dataIndex: 'startedAt',
      defaultSortOrder: 'descend',
    },
    {
      title: 'Balance',
      dataIndex: 'account',
      render: (account: Account) => account?.balance,
    },
    {
      title: 'Commission',
      dataIndex: 'commission',
      key: 'commission',
      render: (commission: number) => commission,
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={investors}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey={(record) => record.id}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfInvestors,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      ></Table>
    </CCardBody>
  )
}
